import JSZip from 'jszip';
import { uploadApi } from '@/apis/uploadApi';
import { processImageUrlForCdn } from '@/utils/imagePolygonManager/imageLoader';
import { normalizeReportInfo } from '@/utils/reportUtils';
import i18n from '@/plugins/i18n';

export const MAX_ZIP_DOWNLOAD_BYTES = 800 * 1024 * 1024; // 800MB
const DEFAULT_IMAGE_ESTIMATE_BYTES = 8 * 1024 * 1024; // 8MB

export function sanitizeFolderName(name) {
  if (!name) return '';
  return name.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export async function downloadImageAsBlob(imageUrl) {
  if (!imageUrl) {
    throw new Error(i18n.t('common.imageUrlRequired'));
  }

  const processedUrl = processImageUrlForCdn(imageUrl);

  if (processedUrl.startsWith('http') && !processedUrl.startsWith(window.location.origin)) {
    try {
      const response = await uploadApi.get(processedUrl, {
        responseType: 'blob'
      });
      return response.data;
    } catch (axiosError) {
      console.warn(`axios 다운로드 실패, fetch로 재시도: ${processedUrl}`, axiosError);
      try {
        const fetchResponse = await fetch(processedUrl, {
          mode: 'cors',
          credentials: 'omit'
        });
        if (!fetchResponse.ok) {
          throw new Error(`HTTP ${fetchResponse.status}`);
        }
        return await fetchResponse.blob();
      } catch (fetchError) {
        console.error(`fetch 다운로드도 실패: ${processedUrl}`, fetchError);
        throw new Error(i18n.t('common.imageDownloadFailed', { reason: fetchError.message }));
      }
    }
  } else {
    const response = await fetch(processedUrl);
    if (!response.ok) {
      throw new Error(i18n.t('common.httpImageDownloadFailed', { status: response.status }));
    }
    return await response.blob();
  }
}

function getKnownImageSizeBytes(image) {
  const candidates = [
    image?.size,
    image?.fileSize,
    image?.imgSize,
    image?.contentLength
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return null;
}

export async function estimateImageSizeBytes(image) {
  const knownSize = getKnownImageSizeBytes(image);
  if (knownSize != null) {
    return knownSize;
  }

  if (!image?.imgUrl) {
    return DEFAULT_IMAGE_ESTIMATE_BYTES;
  }

  const processedUrl = processImageUrlForCdn(image.imgUrl);
  try {
    const response = await fetch(processedUrl, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'omit'
    });
    const contentLength = Number(response.headers.get('content-length'));
    if (Number.isFinite(contentLength) && contentLength > 0) {
      return contentLength;
    }
  } catch (_error) {
    // HEAD 미지원/CORS 차단 시 기본 추정치 사용
  }

  return DEFAULT_IMAGE_ESTIMATE_BYTES;
}

export async function splitImagesByMaxBytes(images, maxBytes = MAX_ZIP_DOWNLOAD_BYTES) {
  if (!Array.isArray(images) || images.length === 0) {
    return { batches: [], totalEstimatedBytes: 0 };
  }

  const batches = [];
  let currentBatch = [];
  let currentBytes = 0;
  let totalEstimatedBytes = 0;

  for (const image of images) {
    const sizeBytes = await estimateImageSizeBytes(image);
    totalEstimatedBytes += sizeBytes;

    if (currentBatch.length > 0 && currentBytes + sizeBytes > maxBytes) {
      batches.push(currentBatch);
      currentBatch = [];
      currentBytes = 0;
    }

    currentBatch.push(image);
    currentBytes += sizeBytes;
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return { batches, totalEstimatedBytes };
}

export async function downloadImagesAsZip(images, folderName, zipFileName) {
  if (!images || images.length === 0) {
    return {
      success: false,
      downloadedCount: 0,
      error: i18n.t('common.noImagesToDownload')
    };
  }

  try {
    const zip = new JSZip();
    const folder = zip.folder(folderName);
    let downloadedCount = 0;
    const errors = [];

    for (const image of images) {
      try {
        if (!image.imgUrl) {
          console.warn(`이미지 URL이 없습니다: ${image.imgName || image.seq || image.id}`);
          continue;
        }

        const blob = await downloadImageAsBlob(image.imgUrl);
        const fileName = image.imgName || `image_${image.seq || image.id}.jpg`;
        folder.file(fileName, blob);
        downloadedCount++;
      } catch (error) {
        const errorMsg = `${i18n.t('common.imageDownloadFailed', { reason: image.imgName || image.seq || image.id })} - ${error.message}`;
        console.error(errorMsg, error);
        errors.push(errorMsg);
      }
    }

    if (downloadedCount === 0) {
      return {
        success: false,
        downloadedCount: 0,
        error: i18n.t('common.noDownloadableImages')
      };
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = window.URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${zipFileName}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      downloadedCount,
      errors: errors.length > 0 ? errors : undefined
    };
  } catch (error) {
    console.error('ZIP 파일 생성 중 오류 발생:', error);
    return {
      success: false,
      downloadedCount: 0,
      error: i18n.t('common.zipCreateFailed', { reason: error.message })
    };
  }
}

export function generateFolderNameFromReport(reportData) {
  if (!reportData) {
    return i18n.t('common.downloadDefaultFolder');
  }

  const isApiResponse = reportData._embedded !== undefined;
  const reportInfo = normalizeReportInfo(reportData, isApiResponse ? 'api' : 'table');
  const siteName = reportInfo.siteName || i18n.t('common.siteNameMissing');
  const reportName = reportInfo.reportName || i18n.t('common.reportNameMissing');
  const examineDate = formatDate(reportInfo.examineDate || reportData.examineDate);

  return `${sanitizeFolderName(siteName)}_${sanitizeFolderName(reportName)}_${examineDate}`;
}

export async function downloadAllIrImages(irImages, reportData) {
  if (!irImages || irImages.length === 0) {
    return {
      success: false,
      downloadedCount: 0,
      error: i18n.t('common.noIrImagesToDownload')
    };
  }

  const folderName = generateFolderNameFromReport(reportData);
  return await downloadImagesAsZip(irImages, folderName, folderName);
}
